import { useEffect, useState } from 'react'
import { Rechenabbruch, api, periodenname, tagesdatum } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { anwendername, satz, t } from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import type { Zeitachsenstand } from './typen'

/**
 * Der Bereich *Perioden* — die Zeitachse des Hauses (2.19, R670–R675).
 *
 * **Ein Bildschirm mit vier Angaben.** Der Zeitraum selbst, wie viel Vorrat
 * bleibt, wie weit der gebuchte Bestand reicht und wer ihn zuletzt gesetzt
 * hat. Jede beantwortet eine eigene Frage; eine fünfte gibt es nicht.
 *
 * **Der Vorrat ist der Grund, warum man hier hinsieht.** Die Entscheidung vom
 * 2026-09-21 hat der Zeitachse ein Ende gegeben — das fängt den Vertipper nach
 * oben ab und läuft dafür ab. Damit das niemanden überrascht, steht die Zahl
 * der verbleibenden Monate da, bevor die erste Lieferung abgewiesen wird.
 *
 * **Abgewiesen wird nicht hier.** Der Riegel steht in der Datenbank (R672) und
 * gilt auch für ein Skript und die Hand an der Konsole. Dieser Bildschirm setzt
 * die Grenze; er ist nicht die Grenze.
 */
export function Perioden() {
  const [stand, setStand] = useState<Zeitachsenstand | null>(null)
  const [geladen, setGeladen] = useState(false)
  const [anfang, setAnfang] = useState<number | null>(null)
  const [ende, setEnde] = useState<number | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  /** Ein fachlicher Satz; eine Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004). */
  const [meldung, setMeldung] = useState<string | null>(null)
  const [befund, setBefund] = useState<Rechenabbruch | null>(null)
  const darf = useDarf()

  useEffect(() => {
    let gilt = true
    api
      .zeitachse()
      .then((z) => {
        if (!gilt) return
        setStand(z)
        setAnfang(z?.anfang ?? null)
        setEnde(z?.ende ?? null)
      })
      .catch((f) => gilt && setMeldung(fachlicherSatz(f)))
      .finally(() => gilt && setGeladen(true))
    return () => {
      gilt = false
    }
  }, [])

  async function setzen() {
    if (anfang === null || ende === null) return

    setLaeuft(true)
    setMeldung(null)
    setBefund(null)
    try {
      const neu = await api.zeitachseSetzen(anfang, ende)
      setStand(neu)
      // Was gelang, sagt das Kästchen oben rechts (7.2 Abschnitt 34, R1130).
      meldeErfolg(t('erfolg.zeitraum.gespeichert'))
    } catch (fehler) {
      // Ursache und Weg kommen als Schlüssel mit Werten und werden hier
      // gesetzt, nicht formuliert (R163) — so wechselt auch eine stehende
      // Meldung die Sprache mit.
      if (fehler instanceof Rechenabbruch) {
        setBefund(fehler)
      } else {
        setMeldung(fachlicherSatz(fehler))
      }
    } finally {
      setLaeuft(false)
    }
  }

  const geaendert = stand !== null && (anfang !== stand.anfang || ende !== stand.ende)

  return (
    <section>
      <h1>{t('bereich.perioden.zeitachse')}</h1>
      <p className="erlaeuterung">{t('perioden.zweck')}</p>

      {meldung && <p className="hinweis">{meldung}</p>}

      {befund && (
        <div className="meldung">
          <p>{satz(befund.meldung)}</p>
          {befund.handreichung && <p className="handreichung">{satz(befund.handreichung)}</p>}
        </div>
      )}

      {geladen && stand === null && (
        <div className="meldung neutral">
          <h2>{t('perioden.ohne.titel')}</h2>
          <p>{t('perioden.ohne.text')}</p>
        </div>
      )}

      {stand !== null && (
        <>
          {/* Wer den Zeitraum nicht setzen darf, liest ihn nur (R1180). */}
          {darf('zeitachseSetzen') ? (
          <div className="zeitachsenfelder">
            {/* MM.JJJJ wie jede Periodeneingabe in der Anwendung (R502). */}
            <Periodenfeld
              wert={anfang}
              aufWert={setAnfang}
              beschriftung={t('perioden.anfang')}
              klasse=""
            />
            <Periodenfeld
              wert={ende}
              aufWert={setEnde}
              beschriftung={t('perioden.ende')}
              klasse=""
            />
            <button
              type="button"
              onClick={setzen}
              disabled={laeuft || !geaendert || anfang === null || ende === null}
            >
              {t('perioden.setzen')}
            </button>
          </div>
          ) : (
            <p className="erlaeuterung">
              {t('perioden.anfang')} {periodenname(stand.anfang)} · {t('perioden.ende')} {periodenname(stand.ende)}
            </p>
          )}

          <Vorrat stand={stand} />

          <p className="erlaeuterung">
            {stand.aeltesteGebucht === null
              ? t('perioden.bestand.leer')
              : t('perioden.bestand', {
                  aelteste: periodenname(stand.aeltesteGebucht),
                  juengste: periodenname(stand.juengsteGebucht ?? stand.aeltesteGebucht),
                })}
          </p>

          {stand.geaendertVon !== null && (
            <p className="erlaeuterung">
              {/* Wer sie gesetzt hat, beim Namen — „system" heißt Einrichtung (R838). */}
              {t('perioden.fassung', {
                wer: anwendername(stand.geaendertVon),
                wann: tagesdatum(stand.geaendertAm),
              })}
            </p>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Wie viele Monate noch bleiben — und ab wann das eine Nachricht ist.
 *
 * **Drei Zustände und nicht einer.** „Noch zwölf Monate" ist eine Auskunft,
 * „noch zwei" ist eine Aufforderung, und „abgelaufen" ist ein Befund: Die
 * nächste Lieferung wird abgewiesen. Ein einziger Satz für alle drei wäre in
 * zwei Fällen der falsche.
 */
function Vorrat({ stand }: { stand: Zeitachsenstand }) {
  /** Ab hier ist der Vorrat eine Nachricht und keine Auskunft mehr. */
  const KNAPP = 3

  if (stand.vorrat === 0) {
    return <p className="hinweis">{t('perioden.vorrat.abgelaufen', { ende: periodenname(stand.ende) })}</p>
  }

  return (
    <p className={stand.vorrat <= KNAPP ? 'hinweis' : 'erlaeuterung'}>
      {t(stand.vorrat <= KNAPP ? 'perioden.vorrat.knapp' : 'perioden.vorrat', {
        vorrat: String(stand.vorrat),
        monate: String(stand.monate),
      })}
    </p>
  )
}
